import { useState } from 'react';
import { Link } from 'react-router-dom';

function Idx({ num, children }) {
  return <span className="cz-section-idx"><strong>{num}</strong>{children}</span>;
}

export default function Contact() {
  const [form, setForm] = useState({
    intent: 'demo',
    name: '', email: '', company: '', size: '50-300',
    role: '', message: '',
  });
  const [submitted, setSubmitted] = useState(false);

  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));
  const intents = [
    { id: 'demo',      label: 'Book a calibrated demo', sub: 'See the instrument with your own org chart.' },
    { id: 'pilot',     label: 'Start a free pilot',      sub: 'Free up to 20 seats, live in 3 weeks.' },
    { id: 'partner',   label: 'Partnership',             sub: 'Agencies, consultants, resellers.' },
    { id: 'media',     label: 'Press or media',          sub: 'Research, interviews, citations.' },
    { id: 'support',   label: 'Customer support',        sub: 'Existing customer with a question.' },
  ];

  const submit = (e) => {
    e.preventDefault();
    // In prod this posts to /api/contact — stub for now
    setSubmitted(true);
  };

  return (
    <>
      {/* ══ HERO ══════════════════════════════════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 72, paddingBottom: 40 }}>
        <div className="cz-wrap">
          <Idx num="00">Correspondence</Idx>
          <h1 className="cz-display cz-display-xl" style={{ marginTop: 18, maxWidth: 1100 }}>
            We <em>reply</em>.<br/>
            That's not a given in SaaS.
          </h1>
          <p className="cz-lede" style={{ marginTop: 24, maxWidth: 66 * 8 }}>
            Every message below routes to a human within one business day.
            No bots, no tiered triage. Pick the reason you're writing and we'll
            meet you there.
          </p>
        </div>
      </section>

      {/* ══ § 01 · CONTACT FORM + DIRECT ══════════════════════════════ */}
      <section className="cz-section" style={{ paddingTop: 24 }}>
        <div className="cz-wrap">
          <div className="contact-layout" style={{
            display: 'grid',
            gridTemplateColumns: '1.4fr 0.9fr', gap: 56,
            alignItems: 'start',
          }}>
            {/* Form */}
            <div style={{ border: '1px solid var(--rule-soft)', background: 'var(--cream)', padding: '34px 34px 38px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', gap: 20, flexWrap: 'wrap' }}>
                <h2 style={{
                  fontFamily: 'var(--ff-display)', fontWeight: 500, fontSize: 32,
                  margin: 0, letterSpacing: '-0.02em',
                }}>Write to the editors</h2>
                <span className="cz-mono-label">Reply time · ≤ 1 business day</span>
              </div>

              {submitted ? (
                <div style={{ marginTop: 28, padding: '28px 24px', border: '1px dashed var(--blue)', background: 'var(--blue-wash)' }}>
                  <p className="cz-mono-label" style={{ color: 'var(--blue)' }}>Received</p>
                  <p style={{
                    fontFamily: 'var(--ff-display)', fontStyle: 'italic', fontSize: 24,
                    margin: '10px 0 0', lineHeight: 1.3,
                    fontVariationSettings: '"opsz" 48, "SOFT" 100, "WONK" 1', color: 'var(--ink)',
                  }}>
                    Thank you, {form.name.split(' ')[0] || 'friend'}. A human will reply
                    inside 24 hours — probably sooner.
                  </p>
                  <button
                    onClick={() => { setSubmitted(false); setForm({ ...form, message: '' }); }}
                    className="cz-btn cz-btn-outline" style={{ marginTop: 22 }}
                  >
                    Send another
                  </button>
                </div>
              ) : (
                <form onSubmit={submit} style={{ marginTop: 28, display: 'flex', flexDirection: 'column', gap: 22 }}>
                  {/* Intent selector */}
                  <div>
                    <p className="cz-mono-label" style={{ marginBottom: 12 }}>I'm writing about</p>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(170px, 1fr))', gap: 8 }}>
                      {intents.map(it => (
                        <label key={it.id} style={{
                          cursor: 'pointer', position: 'relative',
                          padding: '14px 14px 14px 16px',
                          border: `1px solid ${form.intent === it.id ? 'var(--ink)' : 'var(--rule-soft)'}`,
                          background: form.intent === it.id ? 'var(--ink)' : 'transparent',
                          color: form.intent === it.id ? 'var(--cream)' : 'var(--ink)',
                          transition: 'all 0.15s',
                        }}>
                          <input
                            type="radio" name="intent" value={it.id}
                            checked={form.intent === it.id}
                            onChange={set('intent')}
                            style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                          />
                          <span style={{
                            fontFamily: 'var(--ff-display)', fontWeight: 500, fontSize: 15,
                            display: 'block', letterSpacing: '-0.01em',
                          }}>{it.label}</span>
                          <span style={{
                            fontSize: 12, opacity: form.intent === it.id ? 0.85 : 1,
                            color: form.intent === it.id ? 'var(--cream)' : 'var(--muted)',
                            display: 'block', marginTop: 2,
                          }}>{it.sub}</span>
                        </label>
                      ))}
                    </div>
                  </div>

                  {/* Two-col fields */}
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }} className="form-cols">
                    <Field label="Your name" required>
                      <input required type="text" value={form.name} onChange={set('name')} style={inp} placeholder="Anika Rao" />
                    </Field>
                    <Field label="Work email" required>
                      <input required type="email" value={form.email} onChange={set('email')} style={inp} placeholder="anika@acme.com" />
                    </Field>
                    <Field label="Company">
                      <input type="text" value={form.company} onChange={set('company')} style={inp} placeholder="Acme, Inc." />
                    </Field>
                    <Field label="Team size">
                      <select value={form.size} onChange={set('size')} style={inp}>
                        <option value="1-50">1–50 employees</option>
                        <option value="50-300">50–300 employees</option>
                        <option value="300-2000">300–2,000 employees</option>
                        <option value="2000+">2,000+ employees</option>
                      </select>
                    </Field>
                    <Field label="Your role">
                      <input type="text" value={form.role} onChange={set('role')} style={inp} placeholder="VP People, Chief of Staff, etc." />
                    </Field>
                  </div>

                  <Field label="Message" required hint="Tell us what you're trying to solve. Specific &gt; generic.">
                    <textarea
                      required rows={5}
                      value={form.message} onChange={set('message')}
                      style={{ ...inp, resize: 'vertical', minHeight: 120, fontFamily: 'var(--ff-body)', lineHeight: 1.55 }}
                      placeholder={form.intent === 'demo'
                        ? 'Looking to run our first structured calibration cycle in Q3.'
                        : form.intent === 'pilot'
                        ? "We're a 120-person series-B SaaS, Q3 is our first formal cycle."
                        : 'Tell us what you need.'}
                    />
                  </Field>

                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                    <p className="cz-mono-label" style={{ maxWidth: 320 }}>
                      By sending, you agree to our privacy policy. We don't sell data.
                    </p>
                    <button type="submit" className="cz-btn cz-btn-blue cz-btn-lg">
                      Send message <span className="cz-btn-arrow">→</span>
                    </button>
                  </div>
                </form>
              )}
            </div>

            {/* Sidebar — direct channels */}
            <aside style={{ display: 'flex', flexDirection: 'column', gap: 22 }}>
              <div>
                <p className="cz-rule-labeled"><span className="cz-mono-label">Direct channels</span></p>
              </div>

              {[
                ['Sales',     'hello@calibza.com',   'Quotes, pilots, procurement'],
                ['Customer',  'success@calibza.com', 'Existing customer support'],
                ['Security',  'security@calibza.com','Responsible disclosure'],
                ['Press',     'press@calibza.com',   'Media, research, citations'],
                ['Careers',   'jobs@calibza.com',    "Hiring (we're growing)"],
              ].map(([head, email, note]) => (
                <a key={email} href={`mailto:${email}`} style={{
                  display: 'block', padding: '16px 18px',
                  border: '1px solid var(--rule-soft)', background: 'var(--cream)',
                  textDecoration: 'none', color: 'var(--ink)',
                  transition: 'all 0.15s',
                }}
                onMouseEnter={(e) => { e.currentTarget.style.borderColor = 'var(--ink)'; e.currentTarget.style.transform = 'translate(-1px, -1px)'; e.currentTarget.style.boxShadow = '3px 3px 0 var(--blue)'; }}
                onMouseLeave={(e) => { e.currentTarget.style.borderColor = 'var(--rule-soft)'; e.currentTarget.style.transform = ''; e.currentTarget.style.boxShadow = ''; }}
                >
                  <span className="cz-mono-label">{head}</span>
                  <p style={{
                    fontFamily: 'var(--ff-display)', fontWeight: 500, fontSize: 20,
                    margin: '6px 0 2px', letterSpacing: '-0.01em', color: 'var(--blue)',
                  }}>
                    {email}
                  </p>
                  <p style={{ fontSize: 12, color: 'var(--muted)', margin: 0 }}>{note}</p>
                </a>
              ))}

              <div style={{
                padding: '20px 22px', background: 'var(--ink)', color: 'var(--cream)',
                boxShadow: '4px 4px 0 var(--yellow)',
              }}>
                <span className="cz-mono-label" style={{ color: 'var(--yellow)' }}>Calibrated demo</span>
                <p style={{
                  fontFamily: 'var(--ff-display)', fontSize: 22, margin: '10px 0 14px',
                  letterSpacing: '-0.015em', lineHeight: 1.3,
                }}>
                  45 minutes with a founder.
                  Your org chart, our instrument panel.
                </p>
                <a
                  href="https://cal.com/calibza/demo"
                  target="_blank" rel="noopener noreferrer"
                  className="cz-btn cz-btn-cream"
                  style={{ width: '100%', justifyContent: 'center' }}
                >
                  Pick a time <span className="cz-btn-arrow">→</span>
                </a>
              </div>
            </aside>
          </div>
        </div>
      </section>

      {/* ══ § 02 · OFFICES ═══════════════════════════════════════════ */}
      <section className="cz-section" style={{ background: 'var(--cream-warm)', borderBlock: '1px solid var(--rule-soft)' }}>
        <div className="cz-wrap">
          <Idx num="01">Offices</Idx>
          <h2 className="cz-display cz-display-md" style={{ marginTop: 14, maxWidth: 780 }}>
            Remote-first. <em>Three</em> anchors.
          </h2>

          <div className="offices-grid" style={{
            marginTop: 48, display: 'grid',
            gridTemplateColumns: 'repeat(3, 1fr)', gap: 18,
          }}>
            {[
              {
                name: 'San Francisco', tier: 'HQ',
                address: '548 Market St, Suite 2100', city: 'San Francisco, CA 94104',
                coords: 'N 37.7895° / W 122.4008°',
                tz: 'Pacific Time (UTC-8)',
              },
              {
                name: 'New York', tier: 'East Coast',
                address: '379 W Broadway, 5th Floor', city: 'New York, NY 10012',
                coords: 'N 40.7246° / W 74.0021°',
                tz: 'Eastern Time (UTC-5)',
              },
              {
                name: 'London', tier: 'EU',
                address: '70 Wilson Street, EC2A 2DB', city: 'London, United Kingdom',
                coords: 'N 51.5222° / W 0.0866°',
                tz: 'GMT (UTC+0)',
              },
            ].map((o, i) => (
              <article key={o.name} style={{
                border: '1px solid var(--rule-soft)',
                background: 'var(--cream)',
                padding: '26px 26px 28px',
                position: 'relative',
              }}>
                <span style={{
                  position: 'absolute', top: -1, right: -1,
                  padding: '5px 10px',
                  background: i === 0 ? 'var(--blue)' : 'var(--ink)',
                  color: 'var(--cream)',
                  fontFamily: 'var(--ff-mono)', fontSize: 10, letterSpacing: '0.12em',
                }}>{o.tier}</span>

                <h3 style={{
                  fontFamily: 'var(--ff-display)', fontWeight: 500, fontSize: 30,
                  margin: 0, letterSpacing: '-0.02em', lineHeight: 1.05,
                  fontVariationSettings: '"opsz" 48, "SOFT" 60, "WONK" 0',
                }}>{o.name}</h3>

                <p style={{ fontSize: 14, color: 'var(--ink)', margin: '16px 0 0', lineHeight: 1.6 }}>
                  {o.address}<br/>{o.city}
                </p>

                <div style={{ marginTop: 20, paddingTop: 16, borderTop: '1px dashed var(--rule-soft)', display: 'flex', flexDirection: 'column', gap: 6 }}>
                  <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 8 }}>
                    <span className="cz-mono-label">Coords</span>
                    <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--ink)' }}>{o.coords}</span>
                  </div>
                  <div style={{ display: 'grid', gridTemplateColumns: '80px 1fr', gap: 8 }}>
                    <span className="cz-mono-label">Timezone</span>
                    <span style={{ fontFamily: 'var(--ff-mono)', fontSize: 11, color: 'var(--ink)' }}>{o.tz}</span>
                  </div>
                </div>
              </article>
            ))}
          </div>

          <p className="cz-body cz-body-muted" style={{ marginTop: 32, fontSize: 14 }}>
            Ninety percent of the team is remote across 12 countries. The offices above are
            for customer in-persons, offsites, and heads-down sprints. If you're in the area,
            we'd love to get coffee.
          </p>
        </div>
      </section>

      {/* ══ § 03 · RESPONSE PROMISE ══════════════════════════════════ */}
      <section className="cz-section">
        <div className="cz-wrap">
          <div className="two-col" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 64, alignItems: 'center' }}>
            <div>
              <Idx num="02">What happens after you send</Idx>
              <h2 className="cz-display cz-display-md" style={{ marginTop: 14 }}>
                The <em>response</em> protocol.
              </h2>
            </div>

            <ol style={{ listStyle: 'none', padding: 0, margin: 0, display: 'flex', flexDirection: 'column', gap: 0 }}>
              {[
                ['T + 0h',    'Your message lands in the right inbox.', 'Routed by intent, no black-hole catch-all.'],
                ['T + 1h',    'A human reads it.',                       'During business hours. Outside them: next morning.'],
                ['T + 24h',   'You get a reply.',                        'With substance — not a canned acknowledgement.'],
                ['T + 3d',    'If it\'s a demo, it\'s on the calendar.',  'You pick the time. We show up prepared.'],
              ].map(([when, what, body], i) => (
                <li key={when} style={{
                  display: 'grid', gridTemplateColumns: '90px 1fr', gap: 20,
                  padding: '18px 0',
                  borderTop: '1px solid var(--rule-soft)',
                }}>
                  <span className="cz-mono-label" style={{ color: 'var(--blue)' }}>{when}</span>
                  <div>
                    <p style={{
                      fontFamily: 'var(--ff-display)', fontWeight: 500,
                      fontSize: 20, margin: 0, letterSpacing: '-0.01em',
                    }}>{what}</p>
                    <p style={{ fontSize: 13, color: 'var(--muted)', margin: '4px 0 0', lineHeight: 1.55 }}>{body}</p>
                  </div>
                </li>
              ))}
            </ol>
          </div>
        </div>
      </section>

      {/* ══ CTA ═══════════════════════════════════════════════════════ */}
      <section className="cz-section cz-section-dark" style={{ paddingBlock: 96, textAlign: 'center' }}>
        <div className="cz-wrap">
          <h2 className="cz-display cz-display-lg" style={{ maxWidth: 980, marginInline: 'auto' }}>
            Or skip the inbox.<br/>
            <em style={{ color: 'var(--yellow)' }}>Just start building.</em>
          </h2>
          <div style={{ display: 'flex', gap: 12, marginTop: 34, justifyContent: 'center', flexWrap: 'wrap' }}>
            <Link to="/register" className="cz-btn cz-btn-cream cz-btn-lg">
              Start free pilot · 20 seats <span className="cz-btn-arrow">→</span>
            </Link>
            <Link to="/pricing" className="cz-btn cz-btn-outline cz-btn-lg" style={{ boxShadow: 'inset 0 0 0 1px var(--cream)', color: 'var(--cream)' }}>
              See pricing
            </Link>
          </div>
        </div>
      </section>

      <style>{`
        @media (max-width: 1024px) {
          .contact-layout { grid-template-columns: 1fr !important; gap: 32px !important; }
          .offices-grid { grid-template-columns: 1fr !important; }
          .two-col { grid-template-columns: 1fr !important; gap: 32px !important; }
        }
        @media (max-width: 640px) {
          .form-cols { grid-template-columns: 1fr !important; }
        }

        /* Form field focus states */
        .cz-root input:focus,
        .cz-root textarea:focus,
        .cz-root select:focus {
          outline: none;
          border-color: var(--blue);
          box-shadow: 2px 2px 0 var(--blue);
        }
      `}</style>
    </>
  );
}

function Field({ label, required, hint, children }) {
  return (
    <label style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <span style={{
        fontFamily: 'var(--ff-mono)', fontSize: 10, fontWeight: 500,
        letterSpacing: '0.12em', textTransform: 'uppercase', color: 'var(--muted)',
      }}>
        {label}{required && <span style={{ color: 'var(--blue)', marginLeft: 4 }}>·</span>}
      </span>
      {children}
      {hint && <span style={{ fontSize: 11.5, color: 'var(--muted)', fontStyle: 'italic' }}>{hint}</span>}
    </label>
  );
}

const inp = {
  width: '100%',
  padding: '12px 14px',
  border: '1px solid var(--rule-soft)',
  background: 'var(--cream)',
  color: 'var(--ink)',
  fontFamily: 'var(--ff-body)', fontSize: 14,
  borderRadius: 0,
  transition: 'all 0.15s',
};
